import React from 'react';
import {AlertTriangleIcon, PlusIcon, UploadIcon, XIcon} from '../Icons';

export type WordListTab = 'mine' | 'marketplace';

interface WordListsToolbarProps {
  tab: WordListTab;
  onTabChange: (tab: WordListTab) => void;
  myCount: number;
  marketplaceCount: number;
  isImporting: boolean;
  onImport: () => void;
  onCreate: () => void;
  error: string | null;
  onDismissError: () => void;
}

/** The page's primary action, in the one style reserved for it. Also the empty state's CTA. */
export const NewWordListButton: React.FC<{onClick: () => void}> = ({onClick}) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs
     font-medium text-white hover:bg-indigo-500 transition-colors cursor-pointer"
  >
    <PlusIcon className="w-3.5 h-3.5" />
    New word list
  </button>
);

/**
 * Title, the two ways to get a list, and which half of the library you are looking at.
 *
 * My lists and the marketplace are the same table over different rows, so they get a segmented
 * control rather than the five-colour tab strip the desktop build used — a tab strip promises two
 * screens, and there is only one here.
 */
const WordListsToolbar: React.FC<WordListsToolbarProps> = ({
  tab,
  onTabChange,
  myCount,
  marketplaceCount,
  isImporting,
  onImport,
  onCreate,
  error,
  onDismissError,
}) => (
  <>
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold text-gray-50">Word lists</h1>
        <p className="mt-0.5 text-sm text-gray-500">
          Every word in a list gets cut out of the conversions that use it. Matching is
          case-insensitive.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onImport}
          disabled={isImporting}
          title="Import lists from a .csv or .txt file"
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-3 py-1.5
           text-xs font-medium text-gray-200 hover:border-gray-600 hover:bg-gray-800 transition-colors
           disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          <UploadIcon className="w-3.5 h-3.5" />
          {isImporting ? 'Importing…' : 'Import'}
        </button>
        <NewWordListButton onClick={onCreate} />
      </div>
    </div>

    {error && (
      <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/[0.06]
       px-3 py-2.5 text-sm text-red-200">
        <AlertTriangleIcon className="mt-0.5 w-4 h-4 shrink-0" />
        <p className="flex-1">{error}</p>
        <button
          type="button"
          onClick={onDismissError}
          aria-label="Dismiss error"
          className="shrink-0 text-red-300/70 hover:text-red-200 cursor-pointer"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </div>
    )}

    <div className="inline-flex rounded-lg border border-gray-800 bg-gray-900/60 p-0.5">
      <TabButton
        label="My lists"
        count={myCount}
        isActive={tab === 'mine'}
        onClick={() => onTabChange('mine')}
      />
      <TabButton
        label="Marketplace"
        count={marketplaceCount}
        isActive={tab === 'marketplace'}
        onClick={() => onTabChange('marketplace')}
      />
    </div>
  </>
);

const TabButton: React.FC<{
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}> = ({label, count, isActive, onClick}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={isActive}
    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
      isActive ? 'bg-gray-800 text-gray-100' : 'text-gray-500 hover:text-gray-300'
    }`}
  >
    {label}
    <span className={`ml-1.5 tabular-nums ${isActive ? 'text-gray-500' : 'text-gray-600'}`}>
      {count}
    </span>
  </button>
);

export default WordListsToolbar;
